import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/services/api";
import { ShoppingCart, RotateCcw, Package, TrendingUp, Wallet, Loader2, Percent } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;

export default function MayoristaDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [repitiendo, setRepitiendo] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/mayoristas/me/dashboard");
      setData(res.data);
    } catch (e) {
      toast.error("Error cargando el dashboard");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const repetirPedido = async () => {
    if (!data?.ultimo_pedido) return;
    setRepitiendo(true);
    try {
      const res = await api.post(`/mayoristas/me/pedidos/${data.ultimo_pedido.id}/repetir`);
      toast.success(`Pedido ${res.data.numero} creado`);
      navigate("/mayorista/pedidos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al repetir el pedido");
    } finally {
      setRepitiendo(false);
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    );
  }

  const { config, ultimo_pedido, estado_cuenta } = data;

  return (
    <div className="space-y-5" data-testid="mayorista-dashboard">
      <div className="bg-blue-800 text-white rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Percent className="w-4 h-4" />
          <p className="text-sm text-blue-200">Tu condición mayorista</p>
        </div>
        <p className="text-2xl font-bold">
          {config.descuento_tipo === "porcentaje"
            ? `${config.descuento_valor}% de descuento`
            : `${fmt(config.descuento_valor)} de descuento por unidad`}
        </p>
        {(config.minimo_compra_unidades > 0 || config.minimo_compra_valor > 0) && (
          <p className="text-xs text-blue-200 mt-1">
            Compra mínima:{" "}
            {config.minimo_compra_unidades > 0 && `${config.minimo_compra_unidades} unidades`}
            {config.minimo_compra_unidades > 0 && config.minimo_compra_valor > 0 && " / "}
            {config.minimo_compra_valor > 0 && fmt(config.minimo_compra_valor)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Package} label="Inventario disponible" value={`${data.inventario_total} und`} testid="stat-inventario" />
        <StatCard icon={ShoppingCart} label="Solicitudes pendientes" value={data.solicitudes_pendientes} testid="stat-pendientes" />
        <StatCard icon={TrendingUp} label="Margen estimado (mes)" value={fmt(data.margen_estimado_mes)} color="text-green-700" testid="stat-margen" />
        <StatCard icon={Wallet} label="Saldo pendiente" value={fmt(estado_cuenta.saldo_pendiente)} color={estado_cuenta.saldo_pendiente > 0 ? "text-red-600" : "text-green-700"} testid="stat-saldo" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button
          className="bg-blue-800 hover:bg-blue-900 h-14 text-base rounded-2xl"
          onClick={() => navigate("/mayorista/comprar")}
          data-testid="btn-comprar-inventario"
        >
          <ShoppingCart className="w-5 h-5 mr-2" /> Comprar Inventario
        </Button>
        <Button
          variant="outline"
          className="h-14 text-base rounded-2xl border-blue-800 text-blue-800"
          onClick={repetirPedido}
          disabled={!ultimo_pedido || repitiendo}
          data-testid="btn-repetir-pedido"
        >
          {repitiendo ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <RotateCcw className="w-5 h-5 mr-2" />}
          Repetir Último Pedido
        </Button>
      </div>

      {ultimo_pedido && (
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-xs text-gray-500 uppercase mb-2">Último pedido</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-[#4A2C0A]">{ultimo_pedido.numero}</p>
              <p className="text-sm text-gray-500">
                {ultimo_pedido.unidades} unidades · {fmt(ultimo_pedido.total)}
              </p>
            </div>
            <Badge className="bg-blue-100 text-blue-800 border-0 capitalize">{ultimo_pedido.estado}</Badge>
          </div>
        </div>
      )}

      {data.inventario.length > 0 && (
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-xs text-gray-500 uppercase mb-3">Mi inventario</p>
          <div className="space-y-2">
            {data.inventario.map((i) => (
              <div key={i.producto_id} className="flex justify-between text-sm">
                <span className="text-gray-700">{i.producto_nombre}</span>
                <span className="font-semibold text-[#4A2C0A]">{i.cantidad} und</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white rounded-2xl border p-4" data-testid={testid}>
    <Icon className="w-5 h-5 text-blue-800 mb-2" />
    <p className={`text-xl font-bold ${color || "text-[#4A2C0A]"}`}>{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);
